/**
 * Diner-facing copy keyed by coo-api's top-level `error` **slug** — TEAM-15059. The sibling of
 * `@/constants/fpoErrorCopy`, which keys copy by the numeric Chowly FPO error code.
 *
 * ## Why a second catalog exists at all
 *
 * The FPO catalog covers every failure that reaches the storefront carrying a numeric code. Some do
 * not. coo-api's own basket validations — checks it performs itself rather than relaying from an
 * upstream POS — have no `ChowlyFPOErrorCode` to report, so they arrive with `error_data: []` and
 * `error_code: null` and reach **none** of the code-based tiers of the render rule. This catalog is
 * the tier that keys on their semantic slug instead.
 *
 * ## What it does *not* justify itself with (TEAM-15059 review)
 *
 * This header used to claim that without this catalog such failures "could only ever resolve to the
 * call site's static message or `GENERIC_ERROR_COPY`". That reasoning is wrong and the review of
 * this ticket is what caught it: an envelope carrying no numeric code can still carry a
 * `user_message`, which tier 3 renders. The correct claim is narrower — this catalog is the right
 * tier for an envelope that carries **neither a numeric code nor a `user_message`**. Where the
 * backend has authored a sentence, the backend wins and the entry here is `null` so that it can.
 *
 * Which is why **this catalog carries no live copy string today**: its one entry is `null` (see
 * below). The machinery stays because the tier is still the right shape for the next validation
 * coo-api adds without copy of its own — not because anything currently resolves here. One
 * consequence worth knowing before trusting a green suite: in
 * `utils/__tests__/fpoErrorCopy.ts` every shared constraint that skips `null` entries is vacuous
 * over this record until a real sentence lands.
 *
 * ## Why this is not an entry in `FPO_ERROR_COPY`
 *
 * `FPO_ERROR_COPY` is a total `Record<FpoErrorCode, string | null>` and `utils/__tests__/fpoErrorCopy.ts`
 * pins it to "37 codes, contiguous `0`–`36`, all numeric strings". A slug key breaks that test — and
 * that test is the only reason the `DINER_ERROR_COPY_CATALOG_MISS` drift detector means anything,
 * since the warning is trustworthy only while every numeric code is accounted for. Keeping the two
 * key spaces in separate records preserves both guarantees.
 *
 * ## Why keying on the slug is legitimate here
 *
 * docs/DINER_ERROR_MESSAGES.md argues against keying copy on the HTTP `error` slug. That argument is
 * about **granularity**, not principle: it is aimed at the HTTP status names in coo-api's
 * `app/Http/Exceptions/`, where `bad_request` alone covers invalid promo codes, missing gift-card
 * PINs, malformed `wanted_at` values, payment-validation failures and short addresses. One sentence
 * cannot serve all of those.
 *
 * The slugs admitted here are different in kind: each one names exactly one diner situation, so a
 * single sentence is correct for every response that carries it. The distinction to apply when
 * adding an entry is therefore **not** "is it a slug" but:
 *
 * > Does this slug identify one diner situation, or a family of unrelated ones?
 *
 * An HTTP status name (`bad_request`, `access_denied`, `not_found`) always fails that test and must
 * never be added. A semantic slug coined for a single validation passes it.
 *
 * Matching behaviour on `error.error` is long-established in this codebase, not something this
 * catalog introduces. Two named precedents, both load-bearing and both easy to re-check:
 * `hasDedicatedUnavailableItemsUi` (`utils/fpoErrors.ts`) branches on the slug *inside the render
 * rule itself*, and `redux/basket/saga.ts`'s `could_not_create_basket_items_unavailable` branch
 * matches the same semantic-slug shape as the entries below. This catalog only extends that
 * practice from *behaviour* to *copy*.
 *
 * Deliberately **no site count** here. A census in a comment goes stale the first time someone adds
 * a branch, and any number would be soft regardless: `utils/errorReporter.ts` branches on the same
 * `error` field but reads it off a differently-shaped object (`e.json?.error`), so whether that
 * counts as "a site" is a judgement call. Cite the precedents, not the tally.
 *
 * ## The two rules the FPO catalog states, restated because they apply identically
 *
 * 1. **Copy lives here, in code — not in the per-brand Strings config.** Failure copy should not
 *    vary by brand: support recognises the message a customer describes, QA verifies one string, and
 *    no operator can author their own. Do **not** author into
 *    `packages/sdk/src/types/gen/strings.fallback.ts` — it is generated, carries a DO-NOT-MODIFY
 *    banner, and hand edits are wiped by the next `yarn schema:sync`.
 * 2. **There is no same-PR sync obligation with coo-api.** A slug coo-api adds later that is missing
 *    here falls back to tier 3/4/5 copy, exactly as it does today. Unlike the FPO catalog there is
 *    deliberately **no** drift warning for an unmapped slug: every coo-api error response carries an
 *    `error` slug, so warning on the unmapped ones would fire on `bad_request` / `not_found` /
 *    `access_denied` across the whole platform and bury the signal the FPO detector exists to give.
 *
 * @see resolveDinerMessage in `@/utils/fpoErrors` — the canonical render rule
 * @see docs/DINER_ERROR_MESSAGES.md
 */

/**
 * coo-api `error` slugs this catalog carries copy for.
 *
 * Only semantic slugs belong here. See the module comment for the test to apply — an HTTP status
 * name covers a family of unrelated failures and can never have one correct sentence.
 */
export const ERROR_SLUG = {
  /**
   * TEAM-14755 (coo-api): a basket write carried an `option_id` that is no longer on the product's
   * current option list, so the diner's menu is stale. Emitted as a mapped HTTP 422 on both
   * basket-write routes this app reaches — `POST .../baskets` (create) and `PUT .../baskets/{id}`
   * (add item). coo-api performs this check itself, so there is no upstream FPO code to report and
   * the envelope arrives with `error_data: []` and `error_code: null`.
   *
   * It does **not** arrive without diner copy. coo-api#358's `app/Exceptions/Handler.php` has a
   * dedicated `OptionNotOnMenuException` branch that populates `user_title` and `user_message`, and
   * that `user_message` is what the diner reads — see the `null` entry below.
   */
  OptionNotOnMenu: 'option_not_on_menu',
  /**
   * TEAM-15043 (coo-api#368): a basket write left a required option group below its
   * `min_selections`. Almost always a stale cart rather than a diner who skipped a step — the
   * customizer enforces the same minimum at the same nesting depth before an item can be added, but
   * nothing re-checked it when the cart was restored from sessionStorage or replayed by reorder.
   * TEAM-15138 adds that re-check; this entry is for the write that still slips through.
   *
   * Emitted as a mapped 422 on all three basket-write routes. Like the slug above, coo-api performs
   * this check itself, so `error_code` is null — but unlike it, `error_data` is populated, with the
   * option group and the offending cart line. That payload is the reason this slug is worth
   * matching on at all: it is what lets the storefront reopen the right item.
   *
   * Also like the slug above, coo-api authors the sentence. See the `null` entry below.
   */
  RequiredOptionMissing: 'required_option_missing',
} as const;

/** A slug **this catalog knows**. Every one of these has an `ERROR_SLUG_COPY` entry. */
export type ErrorSlug = (typeof ERROR_SLUG)[keyof typeof ERROR_SLUG];

/**
 * Diner copy per slug. `null` means **another source already owns this sentence** — either a
 * dedicated UI that tells the diner what happened, or a lower tier of the render rule that resolves
 * copy for this failure itself. That is `FPO_ERROR_COPY`'s convention, widened by TEAM-15059's
 * review: this catalog's one entry is `null` for the second reason, not the first.
 *
 * The two `null`s are **not** symmetric in effect, and the difference is load-bearing. A `null` in
 * `FPO_ERROR_COPY` silences tier 2 as well (`suppressedByCatalog` in `resolveDinerMessage`), because
 * a dedicated UI is already saying it and a second sentence would double-message. A `null` here
 * deliberately does **not** silence tier 3 — falling through to it is the entire point.
 *
 * Constraints, enforced by `utils/__tests__/fpoErrorCopy.ts` over this record and the FPO one alike:
 * - no `<` or `&` — these strings can reach the global toast, which renders with
 *   `dangerouslySetInnerHTML`
 * - under `FPO_ERROR_COPY_MAX_LENGTH`, so a toast stays readable on a phone
 * - ends in `.` / `!` / `?`, and is never blank
 * - never the substrings `not taking orders` / `store_is_closed` / `store is closed` — coo-api's
 *   `Handler::isRestaurantTemporarilyUnavailable` substring-matches exception messages and would
 *   rewrite the response into the restaurant-unavailable error
 */
export const ERROR_SLUG_COPY: Record<ErrorSlug, string | null> = {
  /**
   * **`null` on purpose: coo-api owns this sentence.** coo-api#358 (TEAM-14755) added a dedicated
   * `OptionNotOnMenuException` branch to `app/Exceptions/Handler.php` which populates the envelope
   * with `user_title: 'Item option unavailable'` and, verbatim:
   *
   * > One of the options you selected is no longer on the menu. Please refresh the menu and choose
   * > from the current options.
   *
   * That is tier 3 of the render rule (`error.user_message`, HTML-escaped by `encode`), and a
   * sentence here would shadow it, because tier 2 outranks tier 3. Two repos would then own one
   * diner-facing sentence and the backend's would be dead with nothing reporting it. Cataloguing the
   * slug as `null` makes tier 2 defer instead — which is what `lookupErrorSlugCopy`'s three-way
   * return exists for, and is deliberate rather than an entry someone forgot to write.
   *
   * **Known wording mismatch, accepted rather than fixed.** coo-api says "refresh **the menu**"; the
   * affordance beside it is a button labelled **"Refresh Page"** that calls
   * `window.location.reload()`. There is no "refresh the menu" control in the storefront —
   * dispatching `menuActions.refreshBasketMenu` repopulates only the basket-menu slice, not the menu
   * the diner is looking at — so a reload is the thing that honours the instruction, just not under
   * that name. Recorded so nobody reads it as an oversight: closing it means a coo-api copy change,
   * and TEAM-15059 asked coo-api for nothing.
   *
   * **The button does not go with the copy.** This slug stays in `RELOAD_RECOVERABLE_SLUGS` below,
   * and that membership — not the presence of a sentence here — is what puts "Refresh Page" on the
   * toast: `redux/basket/saga.ts` calls `isReloadRecoverable(errorResponse.error?.error)` and reads
   * the raw slug. Copy and affordance are now sourced differently, so they are pinned separately:
   * `utils/__tests__/fpoErrorCopy.ts` asserts `isReloadRecoverable` is still true for this slug, and
   * `redux/__tests__/basket.saga.test.ts` asserts the dispatched toast still carries `refresh: true`
   * beside coo-api's sentence.
   */
  [ERROR_SLUG.OptionNotOnMenu]: null,
  /**
   * **`null` on purpose, for the same reason as the entry above: coo-api owns this sentence.**
   * coo-api#368 (TEAM-15043) added a `RequiredOptionMissingException` branch to
   * `app/Exceptions/Handler.php` which populates `user_title: 'Selection needed'` and, verbatim:
   *
   * > This item needs a required choice that is no longer part of your selection. Please reopen it
   * > and choose again.
   *
   * Worth stating explicitly because coo-api#368's own
   * `docs/BASKET_REQUIRED_OPTION_REJECTION.md` asserts that `user_message` is *not* rendered by
   * this app and that the slug and `error_data` are the only live levers. That was true when it was
   * written and stopped being true when TEAM-15059 landed: tier 3 renders `user_message`. Cataloguing
   * a sentence here on the strength of that claim would shadow the backend's, which is precisely the
   * defect TEAM-15059's own review caught. Read `Handler.php`, not the docs that describe it.
   *
   * **This slug is deliberately NOT in `RELOAD_RECOVERABLE_SLUGS`.** coo-api's sentence asks the
   * diner to reopen the item, and a reload reopens nothing — it returns them to the same cart with
   * the same unsatisfied group. The affordance is `basketActions.reopenBasketItemForOptions`,
   * dispatched from the toast and from the checkout reconciliation modal, which resolves the cart
   * line from `error_data` and opens that item's customizer. Copy and affordance are sourced
   * differently here just as they are above, so they are pinned separately.
   */
  [ERROR_SLUG.RequiredOptionMissing]: null,
};

/**
 * The **only** place `ERROR_SLUG_COPY` is indexed, mirroring `lookupFpoErrorCopy`.
 *
 * Three outcomes, all meaningful to the render rule:
 * - a `string` — catalogued copy, show it
 * - `null` — catalogued as "another source owns this sentence" (a dedicated UI, or a lower tier),
 *   fall through silently
 * - `undefined` — not in the catalog at all, fall through silently (**no** drift warning; see the
 *   module comment for why an unmapped slug is not a signal)
 *
 * Accepts the raw `IErrorResponse['error']` value — which is typed `string` but can be absent on a
 * non-coo-api body — rather than an `ErrorSlug`, so callers never have to assert that an arbitrary
 * wire string is a catalog key. That is the same reason `lookupFpoErrorCopy` takes a
 * `WireFpoErrorCode` and not an `FpoErrorCode`.
 *
 * The `hasOwnProperty` guard is **not** defensive boilerplate. The key here is an arbitrary wire
 * string, unlike the FPO catalog's `/^\d+$/`-validated code, so a plain index reaches
 * `Object.prototype`: `ERROR_SLUG_COPY['toString']` is a *function*, which is truthy, and the render
 * rule would return it where a `string` is declared and hand it to the toast's
 * `dangerouslySetInnerHTML`. Own properties only.
 */
export function lookupErrorSlugCopy(slug: string | null | undefined): string | null | undefined {
  if (!slug || !Object.prototype.hasOwnProperty.call(ERROR_SLUG_COPY, slug)) {
    return undefined;
  }
  return (ERROR_SLUG_COPY as Record<string, string | null>)[slug];
}

/**
 * Slugs whose recovery is **reloading the page**.
 *
 * ## Why this lives in the catalog and not at the display site
 *
 * A slug's copy and its affordance are one piece of knowledge, and they have to agree. The
 * `option_not_on_menu` sentence tells the diner to refresh, so the toast it resolves to has to carry
 * the button that does it; conversely an edge 429 resolves to *"please wait about an hour before
 * trying again"* precisely because retrying extends the lockout, and a "Refresh Page" button beside
 * that sentence would invite the one action the copy exists to prevent.
 *
 * For `option_not_on_menu` that agreement is now **cross-repo**, which is the reason to read this
 * twice before adding the next slug: the sentence lives in coo-api (`user_message`, tier 3) and the
 * affordance lives here. Membership in this set is what survives the split — it keys on the slug, so
 * it holds whichever tier produced the copy. See the `null` entry above for the one mismatch that
 * leaves: coo-api says "refresh the menu", the button says "Refresh Page".
 *
 * Before this set, the add-item saga re-derived the answer itself with
 * `errorResponse.error?.error === ERROR_SLUG.OptionNotOnMenu`. That put half the knowledge here and
 * half there with nothing coupling them: a second slug added below would resolve its *copy*
 * correctly through `lookupErrorSlugCopy` and then silently get **no** button, because the saga's
 * inlined comparison would not have been widened to match. Declaring membership next to the copy
 * makes adding a slug one edit instead of two-that-nobody-links.
 *
 * `utils/__tests__/fpoErrorCopy.ts` pins the other half of the invariant: every member here must also
 * be a key in `ERROR_SLUG_COPY`. A slug that offers a reload but has no sentence to offer it beside
 * would be the same silent inconsistency in the other direction.
 *
 * A `Set` of literals rather than a record, which sidesteps `lookupErrorSlugCopy`'s
 * `Object.prototype` hazard entirely: the key here is an arbitrary wire string, and `Set.has` never
 * consults the prototype chain the way `ERROR_SLUG_COPY['toString']` would.
 *
 * @see displayErrorToast's `refresh` flag — `components/toast/index.tsx` renders the button
 */
export const RELOAD_RECOVERABLE_SLUGS: ReadonlySet<ErrorSlug> = new Set([
  /**
   * The diner's menu is stale. A reload re-fetches it, which is what coo-api's tier 3 sentence asks
   * for. Membership here is independent of `ERROR_SLUG_COPY[OptionNotOnMenu]` being `null`.
   */
  ERROR_SLUG.OptionNotOnMenu,
]);

/**
 * Whether a reload is the recovery for this failure — the **only** place
 * `RELOAD_RECOVERABLE_SLUGS` is consulted, mirroring `lookupErrorSlugCopy`'s shape.
 *
 * Takes the raw `IErrorResponse['error']` value (typed `string`, but absent on a non-coo-api body)
 * rather than an `ErrorSlug`, for the same reason `lookupErrorSlugCopy` does: callers never have to
 * assert that an arbitrary wire string is a catalog member. Read it off the raw envelope, never off
 * the resolved `message` — matching on displayed copy is the coupling TEAM-13882 removed.
 */
export function isReloadRecoverable(slug: string | null | undefined): boolean {
  return Boolean(slug) && RELOAD_RECOVERABLE_SLUGS.has(slug as ErrorSlug);
}
